import { useApolloClient } from "@apollo/client";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { bookingsChartQuery } from "../BookingsChart";
import { type IntervalType, CHART_BOOKING_STATUSES } from "../BookingsChart/constants";
import {
  calculateNextDate,
  calculatePreviousDate,
  getBucketCountForInterval,
  getDateRangeForInterval,
} from "../BookingsChart/helpers";
import { Tooltip } from "@/components/Tooltip";
import { Button } from "@/components/ui/button";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { cn } from "@/lib/utils/common";
import { toDisplayDate, toIsoDate } from "@/lib/utils/dates";

export interface ChartDateNavigatorProps {
  referenceDate: Date;
  intervalType: IntervalType;
  onNavigateNext: () => void;
  onNavigatePrevious: () => void;
  onResetToToday: () => void;
  className?: string;
}

export function ChartDateNavigator({
  referenceDate,
  intervalType,
  onNavigateNext,
  onNavigatePrevious,
  onResetToToday,
  className,
}: ChartDateNavigatorProps) {
  const client = useApolloClient();
  const { activeBusinessUnitId } = useActiveBusinessUnit();

  const dateRange = getDateRangeForInterval({
    referenceDate,
    intervalType,
    bucketCount: getBucketCountForInterval(intervalType),
  });

  const handlePreviousHover = () => {
    const previousDate = calculatePreviousDate(referenceDate, intervalType);
    prefetchData(previousDate);
  };

  const handleNextHover = () => {
    const nextDate = calculateNextDate(referenceDate, intervalType);
    prefetchData(nextDate);
  };

  const prefetchData = (date: Date) => {
    const dateRange = getDateRangeForInterval({
      referenceDate: date,
      intervalType,
      bucketCount: getBucketCountForInterval(intervalType),
    });
    void client.query({
      query: bookingsChartQuery,
      variables: {
        businessUnitIds: activeBusinessUnitId ? [activeBusinessUnitId] : undefined,
        plannedShippingDateAfter: toIsoDate(dateRange.start),
        plannedShippingDateBefore: toIsoDate(dateRange.end),
        status: CHART_BOOKING_STATUSES,
      },
    });
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Button variant="outline" onClick={onResetToToday}>
        Today
      </Button>
      <div className="flex items-center gap-2">
        <Tooltip label={`View previous ${getBucketCountForInterval(intervalType)} ${intervalType}`} asChild>
          <Button
            variant="outline"
            onClick={onNavigatePrevious}
            onMouseEnter={handlePreviousHover}
            aria-label="Previous period"
            className="h-10 w-10"
          >
            <ChevronLeft />
          </Button>
        </Tooltip>
        <span className="text-sm font-medium min-w-28 text-center">
          {toDisplayDate(dateRange.start, "MMM d")} - {toDisplayDate(dateRange.end, "MMM d")}
        </span>
        <Tooltip label={`View next ${getBucketCountForInterval(intervalType)} ${intervalType}`} asChild>
          <Button
            variant="outline"
            onClick={onNavigateNext}
            onMouseEnter={handleNextHover}
            aria-label="Next period"
            className="h-10 w-10"
          >
            <ChevronRight />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}
